import { ReactElement, useCallback, useLayoutEffect, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { logger } from "../../App/Logger"
import { useRefCallbacks } from "../../App/useRefCallbacks"
import { classNames } from "../../Components"
import { AlertApi, ILogLighting, ISailChange, ManualShipStatusApi } from "../../Types"
import { ConfirmDialog } from "../ConfirmDialog"
import { Icon, Icons } from "../Icon"
import { Menu, MenuCallbacks } from "../Menu"
import { NavigationLightsIcon, NavigationLightsIconData } from "../NavigationLightsIcon"
import { Select } from "../Select"
import { LogButtonData, LogButtonEngineInfo, LogButtonLightingOption, LogButtonSailInfo, LogButtonSailStaged } from "./Data"
import { lightingIcon as iconForLighting } from "./Lighting"
import { LogButtonSails } from "./Sails"

// Which submenu the menu starts with open. Catalog-only (see `initOpenSubmenu`).
export type LogButtonSubmenu = "status" | "lighting";

function lightingChoiceIcon(lighting: ILogLighting | undefined): ReactElement {
  return NavigationLightsIconData.supports(lighting)
    ? <NavigationLightsIcon lighting={lighting} />
    : <Icon icon={iconForLighting(lighting)} />;
}

// Marks the block that begins each row of the wrapping `.LogButton-blocks` grid
// with `LogButton-block-row-start`.
//
// The stylesheet draws a divider on every block EXCEPT a row-starting one. CSS
// cannot express that on its own: `:first-child` only knows document order, so
// once the menu wraps -- and it does, `Menu.scss` caps it at `calc(100vw - 20px)`
// -- every row but the first would get a divider hanging off its left edge.
// Blocks on the same row share an `offsetTop` (they are stretched to equal
// height), so a change in `offsetTop` is exactly where a new row begins.
//
// Returns a ref callback rather than taking a ref, because the blocks live inside
// the `Menu` and only exist once it is open.
function useBlockRowStarts() {
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  const mark = useCallback(() => {
    if (!node) {
      return;
    }
    let previousTop: number | undefined;
    for (const child of Array.from(node.children)) {
      const block = child as HTMLElement;
      const top = block.offsetTop;
      block.classList.toggle("LogButton-block-row-start", previousTop === undefined || top !== previousTop);
      previousTop = top;
    }
  }, [node]);

  // Re-mark after every render, not just on resize: staging a sail adds the save
  // button and changes a block's height, which can move where the rows break.
  useLayoutEffect(mark);

  useLayoutEffect(() => {
    if (!node) {
      return;
    }
    const observer = new ResizeObserver(mark);
    observer.observe(node);
    window.addEventListener("resize", mark);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", mark);
    };
  }, [node, mark]);

  return setNode;
}

export interface LogButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  createCallback: (status?: any) => void;
  disabled?: boolean;
  engines?: LogButtonEngineInfo[];
  hasNmeaEngineData?: boolean;
  engineStatusCallback?: (engineId: string, on: boolean) => void;
  initConfirmManOverboard?: boolean;
  initOpen?: boolean;
  // Catalog-only seeds. The UI catalog renders a fixture exactly once and cannot
  // script a click, so every state worth previewing needs its own entry point --
  // the same pattern as `initOpen` above. These cover the submenus, whose upward
  // placement (#1220) is only visible while one is open, and the pending-save
  // state that puts the save button at the end of the menu.
  initOpenSubmenu?: LogButtonSubmenu;
  initSelectingSailGroup?: string;
  initStagedSails?: LogButtonSailStaged;
  lightingCallback?: (lighting: ILogLighting) => void;
  lightingValue?: ILogLighting;
  lightingIcon?: ReactElement | string;
  lightingOptions?: LogButtonLightingOption[];
  label: string;
  // Whether the manual ship-status mode (#511) is currently running. Drives the
  // action's on/off appearance and which side of the toggle a press sends.
  manualStatusActive?: boolean;
  onSendAlert?: () => void;
  sails?: LogButtonSailInfo[];
  saveSails?: (changes: ISailChange[]) => void;
  shipStatusIcon: ReactElement | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  shipStatusValue: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  statusOptions: Array<{ icon?: string; title: string; value: any }>;
}

export function LogButton({
  createCallback,
  disabled,
  engines,
  hasNmeaEngineData,
  engineStatusCallback,
  initConfirmManOverboard,
  initOpen,
  initOpenSubmenu,
  initSelectingSailGroup,
  initStagedSails,
  lightingCallback,
  lightingValue,
  lightingIcon,
  lightingOptions,
  label,
  manualStatusActive,
  onSendAlert,
  sails,
  saveSails,
  shipStatusIcon,
  shipStatusValue,
  statusOptions,
}: LogButtonProps) {
  const app = GetApp();
  const t = app.getT("LogButton");
  const menuCallbacks = useRefCallbacks<MenuCallbacks>();
  const blocksRef = useBlockRowStarts();
  const close = useCallback(() => menuCallbacks.current?.setSelected(false), [menuCallbacks]);
  const [confirmManOverboard, setConfirmManOverboard] = useState(initConfirmManOverboard ?? false);

  // Manual man-overboard trigger (#1028, tracker #976): for when there is no N2K emitter, or it
  // fails. Let a failed request propagate so the confirm dialog stays open and shows
  // the error; only dismiss it once the trigger has actually succeeded.
  const triggerManOverboard = useCallback(async () => {
    await new AlertApi(app.apiClient).alert.manOverboard({ params: {} }).call();
    setConfirmManOverboard(false);
  }, [app]);

  // Manual ship-status mode (#511): switch OFF all automatic ship-status changes for
  // a while, so the crew can set the status themselves through the status select
  // above -- for a very slow departure manoeuvre, or while circling in front of a
  // bascule bridge. A real toggle: `manualStatusActive` carries the running mode
  // (derived from the day's open `.manualShipStatus` status by `LogView`), the button
  // renders that state, and pressing it sends the opposite of it. The mode's remaining
  // time and its extend action stay on the UserAdvice button it raises on the current
  // log.
  //
  // No confirmation step, unlike the man-overboard trigger next to it: the mode is
  // harmless, expires by itself (timeout or day change) and is cancellable from that
  // advice too. Fire-and-forget, mirroring the engine/lighting rows, so the menu closes
  // immediately.
  const toggleManualShipStatus = useCallback(() => {
    new ManualShipStatusApi(app.apiClient).manualShipStatus.update({ params: {}, body: { enabled: !manualStatusActive } }).call()
      .catch((err) => logger.error("manual ship status", err));
    close();
  }, [app, close, manualStatusActive]);

  const showEnginesRow = useMemo(
    () => LogButtonData.showEnginesRow(hasNmeaEngineData, engineStatusCallback, engines),
    [hasNmeaEngineData, engineStatusCallback, engines],
  );

  const showSailsRow = useMemo(
    () => LogButtonData.showSailsRow(saveSails, sails),
    [saveSails, sails],
  );

  const showLightingRow = useMemo(
    () => LogButtonData.showLightingRow(lightingCallback, lightingOptions),
    [lightingCallback, lightingOptions],
  );
  const displayedLightingOptions = useMemo(
    () => lightingOptions?.map((option) => ({ ...option, icon: lightingChoiceIcon(option.value) })),
    [lightingOptions],
  );

  const handleEngine = useCallback((id: string, on: boolean) => {
    engineStatusCallback?.(id, on);
    close();
  }, [engineStatusCallback, close]);

  const handleLighting = useCallback((lighting: ILogLighting) => {
    lightingCallback?.(lighting);
    close();
  }, [lightingCallback, close]);

  return (
    <div className="LogButton">
      <div className="LogButton-split">
        <button
          className="LogButton-main"
          disabled={disabled}
          onClick={() => { createCallback(); }}
        >
          <Icon icon={Icons.Plus} />
          {" "}{label}
        </button>

        <div className="LogButton-separator" />

        <Menu
          button={<Icon icon={Icons.ArrowDown} />}
          buttonClass="LogButton-arrow"
          callbacks={menuCallbacks}
          disabled={disabled}
          global={true}
          initOpen={initOpen}
        >
          <div className="LogButton-blocks" ref={blocksRef}>
            <div className="LogButton-block">
              <span className="LogButton-block-label">{t("status")}</span>
              <div className="LogButton-block-buttons">
                {createCallback ? (
                  <Select
                    key={`status-${shipStatusValue}`}
                    button={shipStatusIcon}
                    name="status"
                    value={shipStatusValue}
                    onSelect={createCallback}
                    options={statusOptions}
                    className="LogButton-status-select"
                    initOpen={initOpenSubmenu === "status"}
                  />
                ) : <>{shipStatusIcon}</>}
              </div>
            </div>

            {showEnginesRow && (
              <div className="LogButton-block">
                <span className="LogButton-block-label">{t("engines")}</span>
                <div className="LogButton-block-buttons">
                  {engines!.map((e) => {
                    // off = outline circle, running = filled circle (green), idle = dot-in-ring
                    // (orange) (#619).
                    const state = e.on ? (e.idle ? "idle" : "on") : "off";
                    const icon = !e.on ? Icons.CircleLarge : (e.idle ? Icons.CircleHalfLarge : Icons.CircleFilledLarge);
                    return (
                      <span key={`engine-${e.id}`} className="LogButton-engine-container">
                        <button
                          className={classNames(["button LogButton-engine-btn", state])}
                          title={app.t(`engine_${e.name}`)}
                          onClick={() => handleEngine(e.id, !e.on)}
                        >
                          <Icon
                            className={`LogView-status-icon engine-${state}`}
                            icon={icon}
                          />
                        </button>
                        {e.name && <span className="LogButton-engine-name">{e.name}</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {showSailsRow && (
              <LogButtonSails
                sails={sails!}
                saveSails={saveSails!}
                close={close}
                initSelecting={initSelectingSailGroup}
                initStaged={initStagedSails}
              />
            )}

            {showLightingRow && (
              <div className="LogButton-block LogButton-block-lighting">
                <span className="LogButton-block-label">{t("lighting")}</span>
                <div className="LogButton-block-buttons">
                  <Select
                    key={`lighting-${lightingValue}`}
                    button={lightingIcon ?? lightingChoiceIcon(lightingValue)}
                    name="lighting"
                    value={lightingValue}
                    onSelect={handleLighting}
                    options={displayedLightingOptions!}
                    // Active once a light or day shape is actually shown
                    // (@almute, #916), so the block reads at a glance -- and
                    // `ILogLighting.none` (0) does NOT count, since it is a
                    // recorded value meaning "nothing shown".
                    className={classNames(["LogButton-lighting-select", lightingValue != undefined && lightingValue != ILogLighting.none && "active"])}
                    initOpen={initOpenSubmenu === "lighting"}
                  />
                </div>
              </div>
            )}

            <div className="LogButton-block LogButton-manual-status-block">
              <span className="LogButton-block-label">{t("manual_status")}</span>
              <div className="LogButton-block-buttons">
                <button
                  className={classNames(["button LogButton-manual-status", manualStatusActive && "active"])}
                  title={manualStatusActive ? t("manual_status_active") : t("manual_status")}
                  onClick={toggleManualShipStatus}
                >
                  {/* The running mode wears the icon of the advice it raises, the way
                      the alert and man-overboard actions wear theirs -- the neutral
                      toggle glyph only reads as "switch this on". */}
                  <Icon icon={manualStatusActive ? Icons.AdviceManualShipStatus : Icons.Toggle} />
                </button>
              </div>
            </div>

            {app.hasFeature("multi-user") && onSendAlert && (
              <div className="LogButton-block LogButton-alert-block">
                <span className="LogButton-block-label">{t("send_alert")}</span>
                <div className="LogButton-block-buttons">
                  <button
                    className="button LogButton-send-alert"
                    title={t("send_alert")}
                    onClick={() => { onSendAlert(); close(); }}
                  >
                    <Icon icon={Icons.Alert} className="LogButton-icon" />
                  </button>
                </div>
              </div>
            )}

            <div className="LogButton-block LogButton-mob-block">
              <span className="LogButton-block-label">{t("man_overboard")}</span>
              <div className="LogButton-block-buttons">
                <button
                  className="button LogButton-man-overboard"
                  title={t("man_overboard")}
                  onClick={() => { setConfirmManOverboard(true); close(); }}
                >
                  <Icon icon={Icons.AdviceManOverboard} />
                </button>
              </div>
            </div>
          </div>
        </Menu>
      </div>
      {confirmManOverboard && (
        <ConfirmDialog
          awaitConfirm={true}
          centered={true}
          title={t("man_overboard_confirm_title")}
          message={t("man_overboard_confirm_message")}
          confirmLabel={t("man_overboard_confirm")}
          cancelLabel={t("man_overboard_cancel")}
          errorMessage={t("man_overboard_error")}
          style="alarm"
          onConfirm={triggerManOverboard}
          onCancel={() => setConfirmManOverboard(false)}
        />
      )}
    </div>
  );
}
